import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { Icon } from '../icons/Icon';
import { SelectMenu } from './SelectMenu';

interface FieldShellProps {
  id: string;
  label: string;
  /** Field errors state the problem and the fix on the same line (rule 20). */
  error?: string | undefined;
  hint?: string | undefined;
  /** The label stays for screen readers but is not drawn (a field in a table row). */
  labelHidden?: boolean | undefined;
  children: ReactNode;
}

function FieldShell({ id, label, error, hint, labelHidden, children }: FieldShellProps) {
  return (
    <div className={['fld', error === undefined ? '' : 'invalid'].join(' ').trim()}>
      <label htmlFor={id} className={labelHidden === true ? 'sr' : undefined}>
        {label}
      </label>
      {children}
      {error === undefined ? null : (
        <span className="err" id={`${id}-error`} role="alert">
          <Icon name="alertCircle" size={12} />
          {error}
        </span>
      )}
      {hint === undefined || error !== undefined ? null : (
        <span className="note" id={`${id}-hint`}>
          {hint}
        </span>
      )}
    </div>
  );
}

interface InputFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  id: string;
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
}

export function InputField({ id, label, error, hint, ...rest }: InputFieldProps) {
  return (
    <FieldShell id={id} label={label} error={error} hint={hint}>
      <input
        id={id}
        aria-invalid={error === undefined ? undefined : true}
        aria-describedby={error === undefined ? undefined : `${id}-error`}
        {...rest}
      />
    </FieldShell>
  );
}

interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  id: string;
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  options: readonly { value: string; label: string }[];
  labelHidden?: boolean | undefined;
  /** The drop-down of the DS over the native select (rule 25); without it, the native one. */
  menu?: boolean;
}

/** A select with its label: the native one, or the menu of the DS with the native one kept
 *  under it; keyboard, forms and screen readers keep working (rule 25). */
export function SelectField({
  id,
  label,
  error,
  hint,
  options,
  labelHidden,
  menu = false,
  ...rest
}: SelectFieldProps) {
  const invalid = error === undefined ? undefined : true;
  if (menu)
    return (
      <FieldShell id={id} label={label} error={error} hint={hint} labelHidden={labelHidden}>
        <SelectMenu
          id={id}
          options={options}
          select={{
            ...rest,
            'aria-invalid': invalid,
            'aria-describedby': error === undefined ? undefined : `${id}-error`,
          }}
        />
      </FieldShell>
    );
  return (
    <FieldShell id={id} label={label} error={error} hint={hint} labelHidden={labelHidden}>
      <select id={id} aria-invalid={error === undefined ? undefined : true} {...rest}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

interface TextAreaFieldProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  id: string;
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
}

/** Free text over several lines (an outcome, a reason), with the same label and error line. */
export function TextAreaField({ id, label, error, hint, ...rest }: TextAreaFieldProps) {
  return (
    <FieldShell id={id} label={label} error={error} hint={hint}>
      <textarea
        id={id}
        aria-invalid={error === undefined ? undefined : true}
        aria-describedby={error === undefined ? undefined : `${id}-error`}
        {...rest}
      />
    </FieldShell>
  );
}
